import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Clock, LogOut } from 'lucide-react';

const INACTIVITY_LIMIT = 30 * 60 * 1000; // 30 minutes
const WARNING_BEFORE = 2 * 60 * 1000;    // Show warning 2 min before
const WARNING_AT = INACTIVITY_LIMIT - WARNING_BEFORE; // 28 min

const SessionTimeout = () => {
  const { user, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(120);
  const warningTimer = useRef(null);
  const logoutTimer = useRef(null);
  const countdownInterval = useRef(null);

  const clearAllTimers = useCallback(() => {
    if (warningTimer.current) clearTimeout(warningTimer.current);
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    if (countdownInterval.current) clearInterval(countdownInterval.current);
  }, []);

  const handleLogout = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    logout();
  }, [clearAllTimers, logout]);

  const resetTimers = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    setCountdown(120);

    warningTimer.current = setTimeout(() => {
      setShowWarning(true);
      setCountdown(120);
      countdownInterval.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            handleLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, WARNING_AT);

    logoutTimer.current = setTimeout(() => {
      handleLogout();
    }, INACTIVITY_LIMIT);
  }, [clearAllTimers, handleLogout]);

  const handleActivity = useCallback(() => {
    if (!showWarning) {
      resetTimers();
    }
  }, [showWarning, resetTimers]);

  const handleStayLoggedIn = () => {
    setShowWarning(false);
    resetTimers();
  };

  useEffect(() => {
    if (!user) return;

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, handleActivity));
    resetTimers();

    return () => {
      events.forEach(e => window.removeEventListener(e, handleActivity));
      clearAllTimers();
    };
  }, [user, handleActivity, resetTimers, clearAllTimers]);

  if (!user || !showWarning) return null;

  const minutes = Math.floor(countdown / 60);
  const seconds = countdown % 60;

  return (
    <Dialog open={showWarning} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-600" data-testid="session-warning-title">
            <Clock className="w-5 h-5" />
            Sesiune pe cale de expirare
          </DialogTitle>
          <DialogDescription>
            Din motive de securitate, sesiunea va expira din cauza inactivitatii.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="text-center">
            <div className="text-4xl font-mono font-bold text-orange-600" data-testid="session-countdown">
              {minutes}:{seconds.toString().padStart(2, '0')}
            </div>
            <p className="text-sm text-slate-500 mt-2">
              Veti fi deconectat automat
            </p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleLogout} data-testid="session-logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Deconectare
          </Button>
          <Button onClick={handleStayLoggedIn} className="bg-blue-600 hover:bg-blue-700" data-testid="session-stay-btn">
            Raman conectat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SessionTimeout;
